import { z } from 'zod';

import { responsiveSchema } from '../../utils';
import { backgroundFeature, backgroundFileSchema, borderFeature, borderSideV0Schema } from '../../features';
import { responsiveSpacingSchema } from '../../mixins';

/** The picked background file — the feature's, shared with the column. */
export const blockFileSchema = backgroundFileSchema;

/** Self-versioned, shared with the column — see `features/border`. */
export const blockBorderSideSchema = borderSideV0Schema;
export const blockBorderSchema = borderFeature.fieldSchema;

/**
 * Everything the block paints behind its rows. Self-versioned and shared with
 * the column — see `features/background`; the block is the one that writes the
 * feature's `video` and per-breakpoint `scrollEffect`.
 */
export const blockBackgroundSchema = backgroundFeature.fieldSchema;

/**
 * A decorative divider drawn along one edge of the block. Seven fields that are
 * only meaningful together, so the whole thing is one responsive leaf rather
 * than seven — unlike the contour, no control edits a single field of it.
 *
 * `view` and the two flips are host enums (`EBlockShapeDividerFlip`); the package
 * is framework-free and keeps them as the literals they are persisted as.
 */
export const blockShapeDividerValueSchema = z.object({
    isEnabled: z.boolean(),
    // The rest is absent while the divider is off at this breakpoint — nothing
    // draws it then, and switching it on seeds them (`createShapeDivider`).
    view: z.string().optional(),
    color: z.string().optional(),
    height: z.coerce.number().optional(),
    repeat: z.coerce.number().optional(),
    horizontalFlip: z.string().optional(),
    verticalFlip: z.string().optional(),
});

export const blockShapeDividersSchema = z.object({
    top: responsiveSchema(blockShapeDividerValueSchema),
    bottom: responsiveSchema(blockShapeDividerValueSchema),
});

/**
 * `hideOnDesktop` / `hideOnTablet` / `hideOnMobile`. Flat rather than responsive:
 * this is one switch per breakpoint, not one value read at a breakpoint — all
 * three are read at once to decide where the block renders at all.
 */
export const blockHiddenSchema = z.object({
    desktop: z.boolean(),
    tablet: z.boolean(),
    mobile: z.boolean(),
});

export const blockPaddingSchema = responsiveSpacingSchema;

/**
 * How wide the block lays its rows out, or `null` for the content column every
 * block gets by default — the same reasoning as `height` below: an unset width
 * is an absence, not a number, and the default stays in one place (`blockWidthVar`)
 * instead of being copied into every block that never changed it.
 *
 * `100vw` is the full-bleed spelling, which is what the old `isFullWidth` meant.
 */
export const blockWidthSchema = z.object({
    value: z.coerce.number().default(0),
    unit: z.enum(['px', 'vw']).default('px'),
});

/**
 * How tall the block is, or `null` when its contents decide — which is the
 * ordinary case, and the reason this is nullable rather than a `0` that would be
 * indistinguishable from a real zero.
 *
 * The two subtypes read it differently, and always did: a canvas is exactly this
 * tall, while an ordinary block takes it as a *minimum* and centres its rows in
 * whatever is left. `100vh` is what the old `isFullHeight` boolean meant.
 */
export const blockHeightSchema = z.object({
    value: z.coerce.number().default(0),
    unit: z.enum(['px', 'vh']).default('px'),
});

/**
 * Settings every block subtype carries — the box each of the three paints.
 *
 * Exported as a shape rather than a schema so each subtype can spread it into
 * its own `z.object`; {@link blockCommonDataSchema} below names the same thing
 * as a type, for the accessors that work across all three.
 */
export const blockCommonDataShape = {
    width: blockWidthSchema.nullable(),
    height: responsiveSchema(blockHeightSchema.nullable()),
    /** Sticks the block to the top or the bottom of the viewport while scrolling. */
    pin: z.enum(['top', 'bottom']).nullable(),
    /** Write desktop style edits through to the tablet and mobile tabs. */
    syncWithDesktop: z.boolean(),

    hidden: blockHiddenSchema,
    padding: blockPaddingSchema,
    border: blockBorderSchema,
    background: blockBackgroundSchema,
    shapeDividers: blockShapeDividersSchema,
} as const;

export const blockCommonDataSchema = z.object(blockCommonDataShape);

/** The box every block subtype carries, whichever subtype it is. */
export type BlockCommonProps = z.infer<typeof blockCommonDataSchema>;
